import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchSuppliers } from '@/services/suppliers';
import { fetchPurchases } from '@/services/purchases';
import { processSupplierReturnRpc } from '@/services/supplierReturns';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { Modal } from '@/components/common/Modal';
import {
  Loader2,
  Package,
  Laptop as LaptopIcon,
  Trash2,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';

interface CreateSupplierReturnModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (returnId: string) => void;
}

export const CreateSupplierReturnModal: React.FC<CreateSupplierReturnModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const queryClient = useQueryClient();

  const [supplierId, setSupplierId] = useState('');
  const [purchaseId, setPurchaseId] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [refundAmount] = useState<number>(0);
  const [refundMethod] = useState<'cash' | 'bank_transfer' | 'card'>('cash');
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Return items list
  const [returnItems, setReturnItems] = useState<
    Array<{
      id: string;
      item_type: 'laptop' | 'accessory';
      laptop_id?: string;
      accessory_id?: string;
      name: string;
      unit_cost: number;
      quantity: number;
      condition_on_return: string;
      notes: string;
    }>
  >([]);

  // Suppliers query
  const { data: suppliersData } = useQuery({
    queryKey: ['suppliers', 'dropdown'],
    queryFn: () => fetchSuppliers({ page: 0 }),
    enabled: isOpen,
  });
  const suppliers = suppliersData?.data ?? [];

  // Purchases for selected supplier
  const { data: purchasesData } = useQuery({
    queryKey: ['purchases', 'by-supplier', supplierId],
    queryFn: () => fetchPurchases({ supplierId, page: 0 }),
    enabled: !!supplierId && isOpen,
  });
  const purchases = purchasesData?.data ?? [];

  // Query eligible in-stock laptops for return
  const { data: availableLaptops } = useQuery({
    queryKey: ['laptops', 'eligible-supplier-return', supplierId],
    queryFn: async () => {
      let q = supabase
        .from('laptops')
        .select('id, stock_id, brand, model, serial_number, purchase_price, status')
        .eq('status', 'in_stock');
      if (supplierId) q = q.eq('supplier_id', supplierId);
      const { data, error } = await q.order('stock_id');
      if (error) throw error;
      return data ?? [];
    },
    enabled: isOpen,
  });

  // Query eligible accessories for return
  const { data: availableAccessories } = useQuery({
    queryKey: ['accessories', 'eligible-supplier-return'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('accessories')
        .select('id, sku, name, category, quantity, cost_price, average_cost')
        .gt('quantity', 0)
        .order('name');
      if (error) throw error;
      return data ?? [];
    },
    enabled: isOpen,
  });

  const handleAddLaptop = (lapId: string) => {
    const lap = availableLaptops?.find((l) => l.id === lapId);
    if (!lap) return;
    if (returnItems.some((i) => i.laptop_id === lap.id)) return;

    setReturnItems((prev) => [
      ...prev,
      {
        id: `lap-${lap.id}`,
        item_type: 'laptop',
        laptop_id: lap.id,
        name: `${lap.stock_id} — ${lap.brand} ${lap.model} (S/N: ${lap.serial_number || 'N/A'})`,
        unit_cost: Number(lap.purchase_price) || 0,
        quantity: 1,
        condition_on_return: 'defective',
        notes: '',
      },
    ]);
  };

  const handleAddAccessory = (accId: string) => {
    const acc = availableAccessories?.find((a) => a.id === accId);
    if (!acc) return;
    if (returnItems.some((i) => i.accessory_id === acc.id)) return;

    setReturnItems((prev) => [
      ...prev,
      {
        id: `acc-${acc.id}`,
        item_type: 'accessory',
        accessory_id: acc.id,
        name: `${acc.name} (${acc.sku}) - In Stock: ${acc.quantity}`,
        unit_cost: Number(acc.average_cost || acc.cost_price) || 0,
        quantity: 1,
        condition_on_return: 'faulty',
        notes: '',
      },
    ]);
  };

  const handleRemoveItem = (id: string) => {
    setReturnItems((prev) => prev.filter((i) => i.id !== id));
  };

  const updateItemQty = (id: string, qty: number) => {
    setReturnItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, quantity: Math.max(1, qty) } : i))
    );
  };

  const totalReturnValue = returnItems.reduce(
    (acc, i) => acc + i.unit_cost * i.quantity,
    0
  );

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      if (!supplierId) throw new Error('Please select a supplier.');
      if (returnItems.length === 0) throw new Error('Please add at least one item to return.');
      if (!reason.trim()) throw new Error('Please provide a reason for the supplier return.');

      return processSupplierReturnRpc({
        supplier_id: supplierId,
        purchase_id: purchaseId || null,
        items: returnItems.map((i) => ({
          item_type: i.item_type,
          laptop_id: i.laptop_id ?? null,
          accessory_id: i.accessory_id ?? null,
          quantity: i.quantity,
          unit_cost: i.unit_cost,
          condition_on_return: i.condition_on_return,
          notes: i.notes || null,
        })),
        reason: reason.trim(),
        notes: notes.trim() || null,
        refund_amount: refundAmount > 0 ? refundAmount : null,
        refund_method: refundMethod,
      });
    },
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.purchases.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.laptops.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.accessories.all });
      void queryClient.invalidateQueries({ queryKey: ['suppliers', 'ledger'] });
      void queryClient.invalidateQueries({ queryKey: ['supplier-returns'] });
      onSuccess(res.supplier_return_id);
    },
    onError: (err) => setSubmitError(parseDatabaseError(err)),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    mutate();
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen title="Process Supplier Return / RMA" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4 text-xs">
        {submitError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded text-red-800 font-semibold">
            {submitError}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Supplier */}
          <div>
            <label className="block font-bold text-brand-black mb-1">
              Supplier <span className="text-brand-red">*</span>
            </label>
            <select
              value={supplierId}
              onChange={(e) => {
                setSupplierId(e.target.value);
                setPurchaseId('');
                setReturnItems([]);
              }}
              required
              className="w-full border border-brand-border rounded p-2 text-xs focus:ring-2 focus:ring-brand-green"
            >
              <option value="">-- Select Supplier --</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          {/* Linked Purchase (Optional) */}
          <div>
            <label className="block font-bold text-brand-black mb-1">
              Linked Purchase Invoice (Optional)
            </label>
            <select
              value={purchaseId}
              onChange={(e) => setPurchaseId(e.target.value)}
              disabled={!supplierId}
              className="w-full border border-brand-border rounded p-2 text-xs focus:ring-2 focus:ring-brand-green disabled:bg-gray-100"
            >
              <option value="">-- General Supplier Return (No invoice link) --</option>
              {purchases.map((p) => (
                <option key={p.id} value={p.id}>
                  #{p.purchase_number} ({new Date(p.purchase_date).toLocaleDateString('en-PK')}) — Total: Rs. {p.total.toLocaleString('en-PK')}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Item Selection Dropdowns */}
        <div className="p-3 bg-brand-bg border border-brand-border rounded-lg space-y-3">
          <div className="font-bold text-brand-black flex items-center justify-between">
            <span>Add Items To Return</span>
            <span className="text-[10px] text-brand-muted">Only in-stock inventory items are eligible</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Laptops Selector */}
            <div>
              <label className="text-[10px] font-bold text-brand-muted uppercase flex items-center space-x-1 mb-1">
                <LaptopIcon className="w-3 h-3 text-brand-green" />
                <span>Return In-Stock Laptop</span>
              </label>
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    handleAddLaptop(e.target.value);
                    e.target.value = '';
                  }
                }}
                defaultValue=""
                className="w-full border border-brand-border rounded p-1.5 text-xs focus:ring-2 focus:ring-brand-green"
              >
                <option value="">+ Choose Laptop...</option>
                {availableLaptops?.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.stock_id} — {l.brand} {l.model} (Rs. {l.purchase_price?.toLocaleString('en-PK')})
                  </option>
                ))}
              </select>
            </div>

            {/* Accessories Selector */}
            <div>
              <label className="text-[10px] font-bold text-brand-muted uppercase flex items-center space-x-1 mb-1">
                <Package className="w-3 h-3 text-brand-green" />
                <span>Return In-Stock Accessory</span>
              </label>
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    handleAddAccessory(e.target.value);
                    e.target.value = '';
                  }
                }}
                defaultValue=""
                className="w-full border border-brand-border rounded p-1.5 text-xs focus:ring-2 focus:ring-brand-green"
              >
                <option value="">+ Choose Accessory...</option>
                {availableAccessories?.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} (Qty: {a.quantity}, Avg Cost: Rs. {a.average_cost?.toLocaleString('en-PK')})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Selected Items List */}
        <div className="space-y-2">
          <div className="font-bold text-brand-black text-xs">Return Items ({returnItems.length})</div>
          {returnItems.length === 0 ? (
            <p className="text-brand-muted text-center py-4 bg-gray-50 border border-dashed border-gray-300 rounded">
              No items added to this return yet. Use the dropdowns above.
            </p>
          ) : (
            <div className="border border-brand-border rounded overflow-hidden divide-y divide-brand-border">
              {returnItems.map((item) => (
                <div key={item.id} className="p-2.5 bg-white flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-brand-black truncate">{item.name}</p>
                    <p className="text-[10px] text-brand-muted">
                      Cost: Rs. {item.unit_cost.toLocaleString('en-PK')} • Line Total: Rs. {(item.unit_cost * item.quantity).toLocaleString('en-PK')}
                    </p>
                  </div>

                  {item.item_type === 'accessory' && (
                    <div className="flex items-center space-x-1">
                      <label className="text-[10px] text-brand-muted">Qty:</label>
                      <input
                        type="number"
                        min={1}
                        value={item.quantity}
                        onChange={(e) => updateItemQty(item.id, Number(e.target.value))}
                        className="w-16 border border-brand-border rounded p-1 text-center font-bold"
                      />
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => handleRemoveItem(item.id)}
                    className="p-1 text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Financial Calculation Summary */}
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center justify-between">
          <div>
            <span className="text-[10px] font-bold text-emerald-800 uppercase block">Total Return Value</span>
            <span className="text-base font-black text-emerald-900">Rs. {totalReturnValue.toLocaleString('en-PK')}</span>
          </div>
          <div className="text-right text-[11px] text-emerald-800">
            Automatically credited against supplier payable balance.
          </div>
        </div>

        {/* Reason and Notes */}
        <div>
          <label className="block font-bold text-brand-black mb-1">
            Reason for Return <span className="text-brand-red">*</span>
          </label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Defective motherboard on arrival, incorrect shipment..."
            required
            className="w-full border border-brand-border rounded p-2 text-xs focus:ring-2 focus:ring-brand-green"
          />
        </div>

        <div>
          <label className="block font-bold text-brand-black mb-1">Additional Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="RMA reference number, courier tracking, supplier contact notes..."
            className="w-full border border-brand-border rounded p-2 text-xs focus:ring-2 focus:ring-brand-green resize-none"
          />
        </div>

        {/* Submit */}
        <div className="flex justify-end space-x-2 pt-3 border-t border-brand-border">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-brand-muted hover:text-brand-black text-xs font-semibold"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isPending || returnItems.length === 0 || !supplierId || !reason.trim()}
            className="inline-flex items-center space-x-2 px-5 py-2 bg-emerald-700 text-white font-bold rounded-lg hover:bg-emerald-800 disabled:opacity-50"
          >
            {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            <span>{isPending ? 'Processing Return…' : 'Confirm Supplier Return'}</span>
          </button>
        </div>
      </form>
    </Modal>
  );
};
